import { z } from 'zod';

/**
 * Schema for nested instanties object in search results.
 */
export const InstantieSchema = z.object({
  id: z.string().min(1),
  naam: z.string(),
  afkorting: z.string(),
  niveau: z.string(),
});

/**
 * Schema for rechtsgebied relation item.
 */
export const RechtsgebiedRelationSchema = z.object({
  rechtsgebieden: z.object({
    id: z.string().min(1),
    naam: z.string(),
    slug: z.string(),
  }),
});

/**
 * Schema for zaak_type relation item.
 */
export const ZaakTypeRelationSchema = z.object({
  zaak_types: z.object({
    id: z.string().min(1),
    naam: z.string(),
    code: z.string(),
  }),
});

/**
 * Schema for wetsartikel relation item.
 */
export const WetsartikelRelationSchema = z.object({
  wetsartikelen: z.object({
    id: z.string().min(1),
    volledige_referentie: z.string(),
    artikel_nummer: z.string(),
    wetboek_afkorting: z.string(),
  }),
});

/**
 * Schema for thema relation item.
 */
export const ThemaRelationSchema = z.object({
  themas: z.object({
    id: z.string().min(1),
    naam: z.string(),
    parent_id: z.string().min(1).nullable(),
  }),
});

/**
 * Schema for individual search result item from zoek_jurisprudentie RPC.
 *
 * Matches the new scalar p_portal function signature with OUT parameters.
 */
export const JurisprudentieSearchResultSchema = z.object({
  // Core fields
  id: z.string().min(1),
  ecli: z.string(),
  titel: z.string(),
  samenvatting: z.string().nullable(),
  rechtsvraag: z.string().nullable(),
  oordeel: z.string().nullable(),
  uitspraak_datum: z.string(),
  publicatie_datum: z.string(),
  zaaknummer: z.string().nullable(),

  // Search-specific fields
  relevance_score: z.number(),
  match_type: z.enum(['fts', 'fuzzy', 'none']),

  // Highlight fields (only present for fts matches)
  titel_highlight: z.string().nullable(),
  rechtsvraag_highlight: z.string().nullable(),
  samenvatting_highlight: z.string().nullable(),
  tekst_highlight: z.string().nullable(),
  fuzzy_matched_words: z.union([z.string(), z.array(z.string())]).nullable(),

  // Nested instanties object
  instanties: InstantieSchema.nullable(),

  // Relations for badges
  jurisprudentie_rechtsgebieden: z.array(RechtsgebiedRelationSchema).nullable(),
  jurisprudentie_zaak_types: z.array(ZaakTypeRelationSchema).nullable(),
  jurisprudentie_wetsartikelen: z.array(WetsartikelRelationSchema).nullable(),
  jurisprudentie_themas: z.array(ThemaRelationSchema).nullable(),
});

export type JurisprudentieSearchResult = z.infer<typeof JurisprudentieSearchResultSchema>;

/**
 * Schema for the full zoek_jurisprudentie RPC response.
 */
export const ZoekJurisprudentieResponseSchema = z.object({
  resultaten: z.array(JurisprudentieSearchResultSchema).nullable(),
  totaal_aantal: z.number(),
});

export type ZoekJurisprudentieResponse = z.infer<typeof ZoekJurisprudentieResponseSchema>;

export function validateZoekJurisprudentie(data: unknown): ZoekJurisprudentieResponse {
  return ZoekJurisprudentieResponseSchema.parse(data);
}

export function safeParseZoekJurisprudentie(data: unknown) {
  return ZoekJurisprudentieResponseSchema.safeParse(data);
}

export function validateSearchResult(data: unknown): JurisprudentieSearchResult {
  return JurisprudentieSearchResultSchema.parse(data);
}
